import { defineStore } from "pinia"
import type { components } from "@/assets/api/openapi"
import { cloneDeep } from "lodash-es"

export const useReferenceProfessionsStore = defineStore(
  "referenceProfessionsStore",
  () => {
    const PRACTICAL_KNOWLEDGE_EDUCATION_SUBJECTS_ID = 3

    const { startLoading, stopLoading } = useLoading()
    const appApi = useAppApi()

    const referenceProfession = ref<
      components["schemas"]["ProfessionalGroup"] | undefined
    >(undefined)

    const educationSubjectGroups = ref<
      components["schemas"]["EducationSubjectGroup"][]
    >([])

    const practicalKnowledgeEducationSubjectGroup = computed<
      components["schemas"]["EducationSubjectGroup"] | undefined
    >(() =>
      educationSubjectGroups.value.find(
        ({ id }) => id === PRACTICAL_KNOWLEDGE_EDUCATION_SUBJECTS_ID
      )
    )

    const trainingContentEducationSubjectGroups = computed<
      components["schemas"]["EducationSubjectGroup"][]
    >(() =>
      educationSubjectGroups.value.filter(
        ({ id }) => id !== PRACTICAL_KNOWLEDGE_EDUCATION_SUBJECTS_ID
      )
    )

    const setReferenceProfession = async (id: number) => {
      if (referenceProfession.value && referenceProfession.value.id === id) {
        return
      }

      referenceProfession.value = undefined
      startLoading()

      try {
        const { data, status } = await appApi.getReferenceProfession(id)
        if (status === 200 && data) {
          referenceProfession.value = data
        } else {
          showError({
            statusCode: status === 200 ? 500 : status,
          })
        }
      } catch (error) {
        showError({
          statusCode: 500,
        })
      } finally {
        stopLoading()
      }
    }

    const moveReferenceProfessionSubjectDown = async (currentIndex: number) => {
      const newEducationSubjects = cloneDeep(
        referenceProfession.value?.educationSubjects
      )

      if (!newEducationSubjects?.length) {
        return
      }
      if (currentIndex < newEducationSubjects.length - 1) {
        ;[
          newEducationSubjects[currentIndex],
          newEducationSubjects[currentIndex + 1],
        ] = [
          newEducationSubjects[currentIndex + 1],
          newEducationSubjects[currentIndex],
        ]
      }

      const newSubjectSorting = newEducationSubjects
        .map((subject) => subject.id)
        .filter((id) => id !== undefined)

      await updateReferenceProfessionSubjectSorting(
        newSubjectSorting as number[]
      )
    }

    const moveReferenceProfessionSubjectUp = async (currentIndex: number) => {
      const newEducationSubjects = cloneDeep(
        referenceProfession.value?.educationSubjects
      )

      if (!newEducationSubjects?.length) {
        return
      }
      if (currentIndex > 0) {
        ;[
          newEducationSubjects[currentIndex - 1],
          newEducationSubjects[currentIndex],
        ] = [
          newEducationSubjects[currentIndex],
          newEducationSubjects[currentIndex - 1],
        ]
      }

      const newSubjectSorting = newEducationSubjects
        .map((subject) => subject.id)
        .filter((id) => id !== undefined)

      await updateReferenceProfessionSubjectSorting(
        newSubjectSorting as number[]
      )
    }

    const updateReferenceProfessionSubjectSorting = async (
      subjectSorting: number[]
    ) => {
      if (
        !referenceProfession.value?.id ||
        subjectSorting.length !==
          referenceProfession.value?.educationSubjects?.length
      ) {
        return
      }

      startLoading()

      try {
        const { data, status } =
          await appApi.updateReferenceProfessionSubjectSorting(
            referenceProfession.value.id,
            subjectSorting
          )
        if (status === 200 && data) {
          referenceProfession.value = data
        } else {
          showToast("danger", {
            description:
              "Die Reihenfolge der Fächer konnte nicht geändert werden",
          })
        }
      } catch (error) {
        showToast("danger", {
          description:
            "Die Reihenfolge der Fächer konnte nicht geändert werden",
        })
      } finally {
        stopLoading()
      }
    }

    const fetchEducationSubjectGroupsIfNeeded = async () => {
      if (educationSubjectGroups.value.length) {
        return
      }

      startLoading()

      try {
        const { data, status } =
          await appApi.getReferenceProfessionEducationSubjectGroups()
        if (status === 200 && data) {
          educationSubjectGroups.value = data
        } else {
          showError({
            statusCode: status === 200 ? 500 : status,
          })
        }
      } catch (error) {
        showError({
          statusCode: 500,
        })
      } finally {
        stopLoading()
      }
    }

    return {
      referenceProfession,
      educationSubjectGroups,
      practicalKnowledgeEducationSubjectGroup,
      trainingContentEducationSubjectGroups,
      setReferenceProfession,
      moveReferenceProfessionSubjectDown,
      moveReferenceProfessionSubjectUp,
      fetchEducationSubjectGroupsIfNeeded,
    }
  }
)
